
import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from "react";
import {
  Layout,
  Input,
  Button,
  Tag,
  Row,
  Col,
  Checkbox,
  Modal,
  Select,
  DatePicker,
  Divider,
  Tooltip,
  Upload,
  Icon,
  message,
} from "antd";
import {
  CircledCloseIcon,
  ExpediteTagIcon,
  HazmatTagIcon,
  HighPriorityTagIcon,
  SpecialTagIcon,
  TagCloseIcon,
  TagsModalCloseIcon,
  InfoIconSvg,
  InstructionsAddIcon,
  TagsTooltipIcon,
  TemplatesTooltipIcon
} from "../../common/CustomIcon";
import Button_v2 from "./Button_v2";
import "../../../stylesheets/CreateOrderSidebar.scss";
import ReferenceNumberSection from "./ReferenceNumberSection";
import OptionsSection from "./OptionsSection";
import { marginBottom10 } from "../../helpers/invoice_styles";
import { createTag, fetchTagsFromAPI, saveImportDetails, editImportDetails, uploadCustomsClearanceFile, deleteOrderPic } from "../../api/OrdersApi";
import { OrgContext } from "../../context/OrgContext";
import { getOrderTypeIcon } from "../../components/orders/OrderTypeIcons";
import InfoTooltip from "./InfoTooltip";
import ApprovalRequiredModal from "../../components/configurations/orderApproval/ApprovalRequiredModal";
import moment from "moment";
import MobileInput_v2 from "../../components/common/ReusableMobileInput";

const { TextArea } = Input;
const { Option } = Select;

const CUSTOMS_DOC_TYPES = [
  { code: "DELIVERY_ORDER", label: "delivery order" },
  { code: "CUSTOMS_RELEASE_DOCUMENT", label: "customs release document" },
  { code: "COPY_OF_CHECK_ACH_NUMBER", label: "copy of check" },
  { code: "CUSTOMS_STATUS_LOG", label: "customs status log (from descartes)" },
  { code: "MAWB_HAWB", label: "MAWB/HAWB" },
  { code: "CARGO_MANIFEST", label: "cargo manifest" }
];

const CreateOrderSidebar = ({
  handleSave,
  getDataRef,
  updateOrderDetails,
  orderDetails,
  isLoading,
  onOptionChange,
  onRefNumberChange,
  vehicleTypes=[],
  hazmatClasses=[],
  UNNumber=[],
  referencesData = [],
  details = {},
  setFieldsValue,
  updatedAccountId,
  order_label_code,
  screenMode,
  conditionForOPnOD = false,
  isMultiDay,
  orgRules = {},
  hanldeCloseApprovalModal,
  approvalModal,
  restrictionMessage,
  setRequestApproval,
  isSameDay,
  locations = [],
  delivery_location_details,
  // When the Items & Measure box reports a blocking mismatch, the order
  // Create/Save button is disabled (and its tooltip explains why).
  saveDisabled = false,
  saveDisabledReason = "",
}) => {
  const [tags, setTags] = useState([]);
  const [bondedOrderChecked, setBondedOrderChecked] = useState(false);
  const [inboundChecked, setInboundChecked] = useState(false);
  const [customClearanceChecked, setCustomClearanceChecked] = useState(false);
  const [readyForCustomerChecked, setReadyForCustomerChecked] = useState(false);
  const [isInboundSaved, setIsInboundSaved] = useState(false);
  const [isInboundModalVisible, setIsInboundModalVisible] = useState(false);
  const [isCustomClearanceModalVisible, setIsCustomClearanceModalVisible] = useState(false);

  // Inbound Saved Form State (Original/Persisted)
  const [savedInboundName, setSavedInboundName] = useState("");
  const [savedInboundPhone, setSavedInboundPhone] = useState("");
  const [savedInboundAgentBrokerId, setSavedInboundAgentBrokerId] = useState("");
  const [agentId, setAgentId] = useState("");

  // Inbound Working/Draft State
  const [inboundName, setInboundName] = useState("");
  const [inboundPhone, setInboundPhone] = useState("");
  const [inboundAgentBrokerId, setInboundAgentBrokerId] = useState("");
  const [freightArrivalDate, setFreightArrivalDate] = useState(null);
  const [savedFreightArrivalDate, setSavedFreightArrivalDate] = useState(null);
  const [lastFreeDay, setLastFreeDay] = useState("");
  const [savedLastFreeDay, setSavedLastFreeDay] = useState("");
  const [generalOrderDate, setGeneralOrderDate] = useState(null);
  const [savedGeneralOrderDate, setSavedGeneralOrderDate] = useState(null);
  const [inboundWasCheckedOnOpen, setInboundWasCheckedOnOpen] = useState(false);
  const [isInboundSaving, setIsInboundSaving] = useState(false);

  // Custom Clearance Saved Form State (Original/Persisted)
  const [savedCustomClearanceDate, setSavedCustomClearanceDate] = useState(null);
  const [savedCustomClearancePhone, setSavedCustomClearancePhone] = useState("");
  const [savedCustomClearanceFilesMap, setSavedCustomClearanceFilesMap] = useState({});
  const [savedCustomClearanceFilesUploadedMap, setSavedCustomClearanceFilesUploadedMap] = useState({});
  const [savedCustomClearancePictures, setSavedCustomClearancePictures] = useState([]);
  const [existingCustomsDocsMap, setExistingCustomsDocsMap] = useState({}); // maps docTypeCode to persisted doc
  const [deletedClearanceDocIds, setDeletedClearanceDocIds] = useState([]);
  const [docPreviewVisible, setDocPreviewVisible] = useState(false);
  const [docPreviewUrl, setDocPreviewUrl] = useState(null);
  const [docPreviewType, setDocPreviewType] = useState(null); // 'image' | 'pdf'
  const [isUploadingAll, setIsUploadingAll] = useState(false);

  // Custom Clearance Working/Draft State
  const [customClearanceDate, setCustomClearanceDate] = useState(null);
  const [customClearancePhone, setCustomClearancePhone] = useState("");
  const [customClearanceFilesMap, setCustomClearanceFilesMap] = useState({});
  const [customClearanceFilesUploadedMap, setCustomClearanceFilesUploadedMap] = useState({});
  const [isUploadingCustomClearanceFileMap, setIsUploadingCustomClearanceFileMap] = useState({});
  const [customClearanceWasCheckedOnOpen, setCustomClearanceWasCheckedOnOpen] = useState(false);

  const openInboundModal = useCallback(() => {
    setInboundName(savedInboundName);
    setInboundPhone(savedInboundPhone);
    setInboundAgentBrokerId(savedInboundAgentBrokerId);
    setFreightArrivalDate(savedFreightArrivalDate);
    setLastFreeDay(savedLastFreeDay);
    setGeneralOrderDate(savedGeneralOrderDate);
    setInboundWasCheckedOnOpen(inboundChecked);
    setIsInboundModalVisible(true);
  }, [savedInboundName, savedInboundPhone, savedInboundAgentBrokerId, savedFreightArrivalDate, savedLastFreeDay, savedGeneralOrderDate, inboundChecked]);

  const handleInboundCancel = () => {
    setIsInboundModalVisible(false);
    if (!inboundWasCheckedOnOpen && !agentId) {
      setInboundChecked(false);
    }
  };

  const handleInboundSave = async () => {
    setIsInboundSaving(true);
    try {
      const payload = {
        organization_id: currentOrg?.id || details?.organization_id,
        name: inboundName,
        phone_number: inboundPhone,
        agent_id: inboundAgentBrokerId,
        freight_arrival_date: freightArrivalDate,
        last_free_day: lastFreeDay,
        general_order_date: generalOrderDate,
        customer_order_id: details?.id || details?.customer_order_id || details?._id,
      };

      if (agentId) {
        payload.id = agentId;
      }

      const response = await (agentId ? editImportDetails(payload) : saveImportDetails(payload));
      if (response && response.success) {
        setSavedInboundName(inboundName);
        setSavedInboundPhone(inboundPhone);
        setSavedInboundAgentBrokerId(inboundAgentBrokerId);
        setSavedFreightArrivalDate(freightArrivalDate);
        setSavedLastFreeDay(lastFreeDay);
        setSavedGeneralOrderDate(generalOrderDate);
        
        if (!agentId) {
          const returnedAgentId = response.agent?.id || response.agent?._id || response.agent?.agent_id || inboundAgentBrokerId;
          setAgentId(returnedAgentId);
        }

        setIsInboundSaved(true);
        setIsInboundModalVisible(false);
        message.success(response?.message || "Import Details saved successfully");
      } else {
        const errorMsg = response?.errors?.join(", ") || "Failed to save Import Details";
        message.error(errorMsg);
      }
    } catch (error) {
      console.error("Error saving import details:", error);
      message.error("An error occurred while saving Import Details");
    } finally {
      setIsInboundSaving(false);
    }
  };

  const handleFileSelect = (file, docTypeCode) => {
    setCustomClearanceFilesMap((prev) => ({
      ...prev,
      [docTypeCode]: file,
    }));
    setCustomClearanceFilesUploadedMap((prev) => ({
      ...prev,
      [docTypeCode]: false,
    }));

    // If it's a new order creation (no ID yet), construct the picture object right away
    const orderId = details?.id || details?.customer_order_id || details?._id || "";
    if (!orderId) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64Data = reader.result;
        const ackId = Math.random().toString(36).substring(2, 15) + "_" + Date.now() + "_n";
        const newPicObj = {
          picture_obj: file,
          captured_at: new Date().toISOString(),
          ack_id: ackId,
          pic_title: CUSTOMS_DOC_TYPES.find((t) => t.code === docTypeCode)?.label || "import",
          pic_code: docTypeCode,
          image_type: "import",
          picture_type: "import",
          isNew: true,
          fileType: file.type || "",
          fileName: file.name || "",
          src: base64Data, // Include base64 for creation request
        };

        // Append to savedCustomClearancePictures
        setSavedCustomClearancePictures((prev) => {
          const filtered = (prev || []).filter((p) => p.pic_code !== docTypeCode);
          return [...filtered, newPicObj];
        });
      };
    }
  };

  const handleRemoveFile = (docTypeCode) => {
    setCustomClearanceFilesMap((prev) => {
      const copy = { ...prev };
      delete copy[docTypeCode];
      return copy;
    });
    setCustomClearanceFilesUploadedMap((prev) => {
      const copy = { ...prev };
      delete copy[docTypeCode];
      return copy;
    });
    
    // Also remove from savedCustomClearancePictures
    setSavedCustomClearancePictures((prev) => {
      return (prev || []).filter((p) => p.pic_code !== docTypeCode);
    });
  };

  const handleRemoveExistingDoc = (docTypeCode, docId) => {
    setExistingCustomsDocsMap((prev) => {
      const copy = { ...prev };
      delete copy[docTypeCode];
      return copy;
    });
    if (docId) {
      setDeletedClearanceDocIds((prev) => [...prev, docId]);
    }
  };

  const openCustomClearanceModal = useCallback(() => {
    setCustomClearanceDate(savedCustomClearanceDate);
    setCustomClearancePhone(savedCustomClearancePhone);
    setCustomClearanceFilesMap(savedCustomClearanceFilesMap);
    setCustomClearanceFilesUploadedMap(savedCustomClearanceFilesUploadedMap);
    setCustomClearanceWasCheckedOnOpen(customClearanceChecked);
    setDeletedClearanceDocIds([]);

    const existingDocsMap = {};
    if (locations && locations.length > 0) {
      for (const loc of locations) {
        if (loc?.documents) {
          for (const doc of loc.documents) {
            const docCode = (doc?.pic_code || "").toUpperCase();
            const docTitle = (doc?.pic_title || "").toUpperCase();
            for (const type of CUSTOMS_DOC_TYPES) {
              if (docCode === type.code || docTitle === type.label.toUpperCase() || (type.code === "DELIVERY_ORDER" && docCode === "IMPORT")) {
                if (!existingDocsMap[type.code]) {
                  existingDocsMap[type.code] = doc;
                }
              }
            }
          }
        }
      }
    }
    setExistingCustomsDocsMap(existingDocsMap);
    setIsCustomClearanceModalVisible(true);
  }, [savedCustomClearanceDate, savedCustomClearancePhone, savedCustomClearanceFilesMap, savedCustomClearanceFilesUploadedMap, customClearanceChecked, locations]);

  const handleCustomClearanceCancel = () => {
    setIsCustomClearanceModalVisible(false);
    if (!customClearanceWasCheckedOnOpen && !savedCustomClearanceDate) {
      setCustomClearanceChecked(false);
    }
  };

  const handleInboundCheckboxChange = (checked) => {
    if (checked) {
      setInboundWasCheckedOnOpen(inboundChecked);
      openInboundModal();
      setInboundChecked(true);
    } else {
      if (customClearanceChecked) {
        Modal.confirm({
          title: "Are you sure you want to uncheck Import?",
          content: "Unchecking Import will also uncheck Customs Clearance.",
          okText: "Yes",
          cancelText: "No",
          onOk() {
            setInboundChecked(false);
            setCustomClearanceChecked(false);
          },
          onCancel() {
            // Keep it checked
          }
        });
      } else {
        setInboundChecked(false);
      }
    }
  };

  const handleCustomClearanceCheckboxChange = (checked) => {
    if (!inboundChecked) return;
    if (checked) {
      setCustomClearanceWasCheckedOnOpen(customClearanceChecked);
      openCustomClearanceModal();
      setCustomClearanceChecked(true);
    } else {
      setCustomClearanceChecked(false);
    }
  };

  const handleUploadCustomsFile = (file, docTypeCode, docTypeLabel) => {
    if (!file) return;
    setIsUploadingCustomClearanceFileMap((prev) => ({ ...prev, [docTypeCode]: true }));

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64Data = reader.result;

      const orderId = details?.id || details?.customer_order_id || details?._id || "";
      const locationId = locations?.[0]?.location_id || locations?.[0]?.id || "";
      const ackId = Math.random().toString(36).substring(2, 15) + "_" + Date.now() + "_n";

      const picturesPayload = [
        {
          refer_ids: [orderId],
          item_pictures: [
            {
              picture_obj: file,       // binary
              captured_at: new Date().toISOString(),
              ack_id: ackId,
              pic_title: docTypeLabel,
              pic_code: docTypeCode,
              image_type: "import",
              picture_type: "import",
              isNew: true,
              fileType: file?.type || "",
              fileName: file?.name || "",
            }
          ]
        }
      ];

      const picturesToOverAllUpdate = {
        picture_obj: file,       // binary
        captured_at: new Date().toISOString(),
        ack_id: ackId,
        pic_title: docTypeLabel,
        pic_code: docTypeCode,
        image_type: "import",
        picture_type: "import",
        isNew: true,
        fileType: file?.type || "",
        fileName: file?.name || "",
      };

      const payload = {
        refer: "order",
        order_ids: [orderId],
        refer_ids: [orderId],
        location_id: locationId,
        pictures: picturesPayload,
        pod_pic_settings: [
          {
            pic_code: docTypeCode,
            mandatory: true,
            mandatory_for: [
                "driver",
                "billing"
            ],
            required_for: "both"
          }
        ]
      };

      uploadCustomsClearanceFile(payload)
        .then((res) => {
          if (res && res.success) {
            setCustomClearanceFilesUploadedMap((prev) => ({ ...prev, [docTypeCode]: true }));
            setSavedCustomClearancePictures((prev) => {
              const filtered = (prev || []).filter((p) => p.pic_code !== docTypeCode);
              return [...filtered, picturesToOverAllUpdate];
            });
            message.success(`${docTypeLabel} uploaded successfully`);
          } else {
            message.error(res?.errors || `Failed to upload ${docTypeLabel}`);
          }
        })
        .catch((err) => {
          console.error("Upload error:", err);
          message.error("An error occurred during upload");
        })
        .finally(() => {
          setIsUploadingCustomClearanceFileMap((prev) => ({ ...prev, [docTypeCode]: false }));
        });
    };

    reader.onerror = (error) => {
      console.error("FileReader error:", error);
      message.error("Failed to read file");
      setIsUploadingCustomClearanceFileMap((prev) => ({ ...prev, [docTypeCode]: false }));
    };
  };

  const handleUploadAllCustomsFiles = () => {
    const filesToUpload = CUSTOMS_DOC_TYPES.filter(
      (type) => customClearanceFilesMap[type.code] && !customClearanceFilesUploadedMap[type.code]
    );

    if (filesToUpload.length === 0) return;

    setIsUploadingAll(true);

    const promises = filesToUpload.map((type) => {
      const file = customClearanceFilesMap[type.code];
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          resolve({
            file,
            docTypeCode: type.code,
            docTypeLabel: type.label,
            base64Data: reader.result,
          });
        };
        reader.onerror = (error) => {
          reject(error);
        };
      });
    });

    Promise.all(promises)
      .then((fileDatas) => {
        const orderId = details?.id || details?.customer_order_id || details?._id || "";
        const locationId = locations?.[0]?.location_id || locations?.[0]?.id || "";

        const itemPictures = [];
        const podPicSettings = [];
        const picturesToOverAllUpdateList = [];

        fileDatas.forEach(({ file, docTypeCode, docTypeLabel, base64Data }) => {
          const ackId = Math.random().toString(36).substring(2, 15) + "_" + Date.now() + "_n";
          itemPictures.push({
            picture_obj: file,
            captured_at: new Date().toISOString(),
            ack_id: ackId,
            pic_title: docTypeLabel,
            pic_code: docTypeCode,
            image_type: "import",
            picture_type: "import",
            isNew: true,
            fileType: file?.type || "",
            fileName: file?.name || "",
            src: base64Data,
          });

          podPicSettings.push({
            pic_code: docTypeCode,
            mandatory: true,
            mandatory_for: ["driver", "billing"],
            required_for: "both",
          });

          picturesToOverAllUpdateList.push({
            picture_obj: file,
            captured_at: new Date().toISOString(),
            ack_id: ackId,
            pic_title: docTypeLabel,
            pic_code: docTypeCode,
            image_type: "import",
            picture_type: "import",
            isNew: true,
            fileType: file?.type || "",
            fileName: file?.name || "",
          });
        });

        const payload = {
          refer: "order",
          order_ids: [orderId],
          refer_ids: [orderId],
          location_id: locationId,
          pictures: [
            {
              refer_ids: [orderId],
              item_pictures: itemPictures,
            }
          ],
          pod_pic_settings: podPicSettings,
        };

        return uploadCustomsClearanceFile(payload)
          .then((res) => {
            if (res && res.success) {
              setCustomClearanceFilesUploadedMap((prev) => {
                const next = { ...prev };
                filesToUpload.forEach((type) => {
                  next[type.code] = true;
                });
                return next;
              });
              setSavedCustomClearancePictures((prev) => {
                const codes = filesToUpload.map((t) => t.code);
                const filtered = (prev || []).filter((p) => !codes.includes(p.pic_code));
                return [...filtered, ...picturesToOverAllUpdateList];
              });
              message.success("All documents uploaded successfully");
            } else {
              message.error(res?.errors || "Failed to upload documents");
            }
          });
      })
      .catch((err) => {
        console.error("Upload all error:", err);
        message.error("An error occurred during upload");
      })
      .finally(() => {
        setIsUploadingAll(false);
      });
  };

  const handleCustomClearanceSave = () => {
    const orderId = details?.id || details?.customer_order_id || details?._id || "";
    if (orderId && deletedClearanceDocIds.length > 0) {
      const deletePromises = deletedClearanceDocIds.map((ackId) => {
        const data = {
          refer: "order",
          refer_id: orderId,
          ack_id: ackId,
          account_id: details?.account_id || details?.customer_order_details?.account_id,
          organization_id: currentOrg?.id || details?.organization_id,
        };
        return deleteOrderPic(data);
      });

      Promise.all(deletePromises)
        .then(() => {
          setDeletedClearanceDocIds([]);
        })
        .catch((err) => {
          console.error("Error deleting documents:", err);
        });
    }

    setSavedCustomClearanceDate(customClearanceDate);
    setSavedCustomClearancePhone(customClearancePhone);
    setSavedCustomClearanceFilesMap(customClearanceFilesMap);
    setSavedCustomClearanceFilesUploadedMap(customClearanceFilesUploadedMap);
    setIsCustomClearanceModalVisible(false);
    setCustomClearanceChecked(true);
  };

  const [notes, setNotes] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [templateInput, setTemplateInput] = useState("");
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);
  const [isRecurringOrder, setIsRecurringOrder] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [repeatOption, setRepeatOption] = useState("Daily");
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedMonthlyDate, setSelectedMonthlyDate] = useState(null);
  const [specificDate, setSpecificDate] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [includePickerOpen, setIncludePickerOpen] = useState(false);
  const [includedDates, setIncludedDates] = useState([]);
  const [templateError, setTemplateError] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [hoveredField, setHoveredField] = useState(null);

 
  const referenceRef = useRef(null);
  const optionsRef = useRef(null);
  const [customTags , setCustomTags] = useState([]);
  const { currentOrg, orgSettings } = useContext(OrgContext);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRequestApproval = async() => {
    await setRequestApproval(true);
    handleSave();
  };

  useEffect(() => {
    const fetchTags = async () => {
        const organizationId = currentOrg?.id;
        try {
          const response = await fetchTagsFromAPI(organizationId);
            if (response?.success && Array.isArray(response.tags)) {
              setCustomTags(response.tags.map(tag => ({
                    id: tag.id,
                    name: tag.tag_name,
                    description: tag.tag_description,
                })));
            } else {
              setCustomTags([]);
            }
        } catch (error) {
            console.error("Failed to fetch tags:", error);
            setCustomTags([]);
        }
    };
    fetchTags();
}, [currentOrg, refreshKey]);

  const instructionsRef = useRef(null);
  const tagsRef = useRef(null);
  const sidebarRef = useRef(null);

  const [refAvailableSpace, setRefAvailableSpace] = useState(false);
  const [optionsAvailableSpace, setOptionsAvailableSpace] = useState(false);
  useEffect(() => {
    const calculateAvailableSpace = () => {
      if (
        instructionsRef.current &&
        tagsRef.current &&
        sidebarRef.current
      ) {
        const windowHeight = window.innerHeight;  // Full height of the viewport
        const sidebarRect = sidebarRef.current.getBoundingClientRect(); // Sidebar position relative to viewport
  
        // Calculate the height of instructions and tags sections
        const instructionsHeight = instructionsRef.current.getBoundingClientRect().height;
        const tagsHeight = tagsRef.current.getBoundingClientRect().height;
  
        // Subtract the heights of instructions and tags from the viewport height
        const availableSpace = windowHeight - instructionsHeight - tagsHeight - 200; // 200px accounts for any other necessary spacing like margins or padding
  
       // console.log("Available space before overflow:", availableSpace);
  
        setRefAvailableSpace(availableSpace >= 500);  // Check if available space meets the required condition
        setOptionsAvailableSpace(availableSpace >= 700);  // Check for another condition
      }
    };
  
    calculateAvailableSpace();  // Initial calculation on mount
    window.addEventListener("resize", calculateAvailableSpace);  // Recalculate on window resize
  
    return () => window.removeEventListener("resize", calculateAvailableSpace);  // Cleanup on unmount
  }, [instructionsRef, tagsRef, sidebarRef]);
  
  useEffect(() => {
    if (getDataRef) {
      getDataRef.current = () => ({
        notes,
        template_name: templateName || "",
        is_template: isChecked,
        tags: tags,
        is_inbound: bondedOrderChecked,
        is_imported: inboundChecked,
        is_customs_cleared: customClearanceChecked,
        ready_for_customer: readyForCustomerChecked,
        freight_arrival_date: savedFreightArrivalDate || freightArrivalDate || null,
        last_free_day: savedLastFreeDay || lastFreeDay || "",
        general_order_date: savedGeneralOrderDate || generalOrderDate || null,
        customs_cleared_date: savedCustomClearanceDate || customClearanceDate || null,
        customs_cleared_by: savedCustomClearancePhone || customClearancePhone || "",
        customClearancePictures: savedCustomClearancePictures || [],
        isRecurringOrder,
        ...(isRecurringOrder && repeatOption && {
          template_configs: {
            config: {
              ...(repeatOption === "Daily" && {
                daily: [
                  "SUNDAY",
                  "MONDAY",
                  "TUESDAY",
                  "WEDNESDAY",
                  "THURSDAY",
                  "FRIDAY",
                  "SATURDAY",
                 ],
              }),
              ...(repeatOption === "Days" && { days: selectedDays }),
              ...(repeatOption === "Monthly" && { monthly: selectedMonthlyDate }),
              ...(repeatOption === "Specific Date" && { date: specificDate }),
            },
            holidays: selectedDates,
            en_order_dates: includedDates,
          },
        }),
      });
    }
  }, [
    templateName,
    isChecked,
    tags,
    repeatOption,
    selectedDays,
    selectedMonthlyDate,
    specificDate,
    includedDates,
    selectedDates,
    referencesData,
    getDataRef,
    notes,
    isRecurringOrder,
    bondedOrderChecked,
    inboundChecked,
    customClearanceChecked,
    savedFreightArrivalDate,
    freightArrivalDate,
    savedLastFreeDay,
    lastFreeDay,
    savedGeneralOrderDate,
    generalOrderDate,
    savedCustomClearanceDate,
    customClearanceDate,
    savedCustomClearancePhone,
    customClearancePhone,
    savedCustomClearancePictures,
    readyForCustomerChecked,
  ]);

  useEffect(() => {
    let initialTags = details.tags ? [...details.tags] : [];
    initialTags = initialTags.filter(t => t?.toLowerCase() !== "inbound" && t?.toLowerCase() !== "custom clearance");
    setTags(initialTags);

    setBondedOrderChecked(!!details.is_inbound);

    const isImported = !!details.is_imported;
    const isCustomsCleared = !!details.is_customs_cleared;
    setInboundChecked(isImported);
    setCustomClearanceChecked(isCustomsCleared);
    const orderId = details?.id || details?._id;
    const isReady = !!details.ready_for_customer || (orderId && localStorage.getItem('ready_for_customer_' + orderId) === 'true');
    setReadyForCustomerChecked(!!isReady);

    const agent = details.import_order_agent_details || 
                  details.import_order_deliver_agent_details;

    if (isImported) {
      if (agent) {
        setAgentId(agent.id || agent._id || agent.agent_id || "");
        setSavedInboundName(agent.name || "");
        setInboundName(agent.name || "");
        setSavedInboundPhone(agent.phone_number || agent.phone || "");
        setInboundPhone(agent.phone_number || agent.phone || "");
        setSavedInboundAgentBrokerId(agent.agent_id || "");
        setInboundAgentBrokerId(agent.agent_id || "");
      }
      const arrivalDate = (agent && agent.freight_arrival_date) || details.freight_arrival_date || null;
      const freeDay = (agent && agent.last_free_day) || details.last_free_day || "";
      const generalDate = (agent && agent.general_order_date) || details.general_order_date || null;
      setSavedFreightArrivalDate(arrivalDate);
      setFreightArrivalDate(arrivalDate);
      setSavedLastFreeDay(freeDay);
      setLastFreeDay(freeDay);
      setSavedGeneralOrderDate(generalDate);
      setGeneralOrderDate(generalDate);
      setIsInboundSaved(true);
    } else {
      setAgentId("");
      setSavedInboundName("");
      setInboundName("");
      setSavedInboundPhone("");
      setInboundPhone("");
      setSavedInboundAgentBrokerId("");
      setInboundAgentBrokerId("");
      setSavedFreightArrivalDate(null);
      setFreightArrivalDate(null);
      setSavedLastFreeDay("");
      setLastFreeDay("");
      setSavedGeneralOrderDate(null);
      setGeneralOrderDate(null);
      setIsInboundSaved(false);
    }

    if (isCustomsCleared) {
      const customsDate = details.customs_cleared_date || null;
      const customsPhone = details.customs_cleared_by || "";
      setSavedCustomClearanceDate(customsDate);
      setCustomClearanceDate(customsDate);
      setSavedCustomClearancePhone(customsPhone);
      setCustomClearancePhone(customsPhone);
    } else {
      setSavedCustomClearanceDate(null);
      setCustomClearanceDate(null);
      setSavedCustomClearancePhone("");
      setCustomClearancePhone("");
    }

    if (details?.notes) {
      const formattedNotes = details.notes.replace(/<br\s*\/?>/gi, '\n');
      setNotes(formattedNotes);
    }
  }, [details]); 

  const handleRepeatChange = (value) => {
    setRepeatOption(value);
    setSelectedDays([]);
    setSelectedMonthlyDate(null);
    setSpecificDate(null);
  };

  const handleDaysChange = (selectedValues) => {
    setSelectedDays(selectedValues);
  };


  const handleMonthlyDateChange = (date) => {
    setSelectedMonthlyDate(date);
  };

  const handleSpecificDateChange = (date, dateString) => {
    setSpecificDate(dateString);
  };


  const handleDateSelect = (date, dateString) => {
    if (!date) return;
    const formattedDate = dateString;

    if (selectedDates.includes(formattedDate)) {
      setSelectedDates(selectedDates.filter((d) => d !== formattedDate));
    } else {
      setSelectedDates([...selectedDates, formattedDate]);
    }
  };
  const handleOkClick = () => {
    setPickerOpen(false);
  };
  const handleIncludesOkClick = () => {
    setIncludePickerOpen(false);
  };
  const removeDate = (dateToRemove) => {
    setSelectedDates(selectedDates.filter((date) => date !== dateToRemove));
  };
  const removeIncludedDate = (dateToRemove) => {
    setIncludedDates(includedDates.filter((date) => date !== dateToRemove));
  };

  const dateRender = (current) => {
    const dateString = current.format("YYYY-MM-DD");
    const isSelected = selectedDates.includes(dateString);

    return (
      <div
        className="ant-picker-cell-inner"
        style={{
          backgroundColor: isSelected ? "#57ce61" : undefined,
          color: isSelected ? "white" : undefined,
          borderRadius: "50%",
          width: "24px",
          height: "24px",
          lineHeight: "24px",
        }}
      >
        {current.date()}
      </div>
    );
  };

  const includeDateRender = (current) => {
    const dateString = current.format("YYYY-MM-DD");
    const isIncluded = includedDates.includes(dateString);

    return (
      <div
        className="ant-picker-cell-inner"
        style={{
          backgroundColor: isIncluded ? "#57ce61" : undefined,
          color: isIncluded ? "white" : undefined,
          borderRadius: "50%",
          width: "24px",
          height: "24px",
          lineHeight: "24px",
        }}
      >
        {current.date()}
      </div>
    );
  };

  const handlePickerOpen = (open) => {
    setPickerOpen(true);
  };
  const handleIncludesPickerOpen = (open) => {
    setIncludePickerOpen(true);
  };
  const weekDaysOptions = [
    "SUNDAY",
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
  ];

  const getTagIcon = (tag) => {
    switch (tag?.toLowerCase()) {
      case "special":
      case "pickup_special":
      case "delivery_special":
      case "linehaul_special":
        return (
          <div style={{ paddingRight: "3px" }} className="center">
            <SpecialTagIcon />
          </div>
        );
      case "hazmat":
        return (
          <div style={{ paddingRight: "3px" }} className="center">
            <HazmatTagIcon />
          </div>
        );
      case "expedite":
        return (
          <div style={{ paddingRight: "3px" }} className="center">
            <ExpediteTagIcon />
          </div>
        );
      case "priority":
        return (
          <div style={{ paddingRight: "5px" }} className="center">
            <HighPriorityTagIcon />
          </div>
        );
      default:
        return null;
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags((prevTags) => prevTags.filter((tag) => tag !== tagToRemove));
  };

  
  const handleCheckboxChange = (e) => {
    if (e.target.checked) {
      setIsTemplateModalVisible(true);
    } else {
      setIsChecked(false);
      setTemplateName("Save as Template");
    }
  };

  const handleSaveTemplate = useCallback(() => {
    if (templateInput.trim()) {
      setTemplateName(`${templateInput}`);
      setIsChecked(true);
      setIsTemplateModalVisible(false);
      setTemplateInput("");
      // setIsRecurringOrder(false);
      setTemplateError("");
    } else {
      setTemplateError("Template Name is required");
    } 
  }, [templateInput, isChecked]);

  const handleModalCancel = () => {
    setIsTemplateModalVisible(false);
    setTemplateInput("");
    setIsChecked(false);
    // setIsRecurringOrder(false);
  };
  const handleTagSubmit = () => {
    if (tagInput && !availableTags.includes(tagInput)) {
      setAvailableTags((prev) => [...prev, tagInput]);
    }
    handleTagCheckboxChange(tagInput);
    setTagInput("");
  };

  const handleTagCheckboxChange = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const addSelectedTags = () => {
    setTags([...new Set(selectedTags)]);
    setIsModalVisible(false);
    setTagInput("");
  };

  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };
  const openModal = (e) => {
    const rowContainer = e.currentTarget.closest('.row-container');
    const position = rowContainer
      ? rowContainer.getBoundingClientRect()
      : e.target.getBoundingClientRect();

      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
    
      const offsetTop = -150; 
      const offsetLeft = -20;
    
      setModalPosition({
        top: Math.min(position.bottom, viewportHeight - 16) + offsetTop,
        left: Math.min(position.right, viewportWidth - 224 - 16) + offsetLeft,
      });
    setSelectedTags([...tags]);
    setIsModalVisible(true);
  };
  const handleTemplateInputChange = (e) => {
    setTemplateInput(e.target.value);
    if (templateError) {
      setTemplateError("");
    }
  };

  const handleRecurringCheckboxChange = (e) => {
    setIsRecurringOrder(e.target.checked);
  };

  const filteredTags = useMemo(() => {
    return tags.filter((tag) => tag !== "special")?.map((tag, index) => (
      <Tag key={index} className="tag textCapitalize">
        {getTagIcon(tag)}
        {_.startCase(tag)}
        {!["special", "hazmat", "priority", "expedite", "pickup_special", "delivery_special", "linehaul_special"].includes(
          tag?.toLowerCase()
        ) && (
            <span className="close-icon" onClick={() => handleRemoveTag(tag)}>
              <TagCloseIcon width="12" height="12" />
            </span>
          )}
      </Tag>
    ));
  }, [tags, getTagIcon, handleRemoveTag]);

  const filteredAvailableTags = tagInput
  ? availableTags.filter((tag) =>
      tag.toLowerCase().includes(tagInput.toLowerCase())
    )
  : availableTags;

  const sortedAvailableTags = [
    ...selectedTags,
    ...customTags
      .map((tag) => tag.name)
      .filter((tag) => !selectedTags.includes(tag)),
    ...availableTags.filter(
      (tag) =>
        !selectedTags.includes(tag) &&
        !customTags.some((customTag) => customTag.name === tag)
    ),
  ]
    .filter((tag, index, self) => self.indexOf(tag) === index)
    .filter((tag) => tag.toLowerCase().includes(tagInput.toLowerCase()))
    .filter(tag => !tag.toLowerCase().includes("special"))
    .sort((a, b) => {
      const isASelected = selectedTags.includes(a);
      const isBSelected = selectedTags.includes(b);
      if (isASelected && isBSelected) return 0;
      if (isASelected) return -1; 
      if (isBSelected) return 1;
      return a.localeCompare(b);
    });
  
  



    const handleRefNumberChange = (newRefNumbersData) => {
      if (onRefNumberChange) {
        onRefNumberChange(newRefNumbersData);
      }
    };

   const handleOptionsChange = (newOptionsArray, newVehicleType, selectedColor, selectedClass, selectedSubClass, isHazardous, waitTime, selectedUNNumbers) => {
    if (onOptionChange) {
      onOptionChange(newOptionsArray, newVehicleType, selectedColor, selectedClass, selectedSubClass, isHazardous, waitTime, selectedUNNumbers);
    }
  };

  const isPhoneEmpty = (phone) => {
    if (!phone) return true;
    const digits = phone.replace(/\D/g, "");
    return digits === "1" || digits === "91" || digits === "52" || digits === "";
  };

  const isInboundSaveDisabled =
    !inboundName?.trim() ||
    isPhoneEmpty(inboundPhone) ||
    !inboundAgentBrokerId?.trim() ||
    !freightArrivalDate ||
    !lastFreeDay ||
    !generalOrderDate ||
    isInboundSaving;

  const orderId = details?.id || details?.customer_order_id || details?._id || "";
  const allDocsUploaded = CUSTOMS_DOC_TYPES.every((type) => {
    const existingDoc = existingCustomsDocsMap[type.code];
    const newFile = customClearanceFilesMap[type.code];
    const newFileUploaded = customClearanceFilesUploadedMap[type.code];
    return existingDoc || (newFile && (!orderId || newFileUploaded));
  });
  const anyUnuploadedDocs = orderId ? Object.keys(customClearanceFilesMap).some(code => {
    return customClearanceFilesMap[code] && !customClearanceFilesUploadedMap[code];
  }) : false;
  const isCustomClearanceSaveDisabled = !customClearanceDate || anyUnuploadedDocs;

  return (
    <div ref={sidebarRef}>
    <Layout className="create-order-sidebar">
      <div className="create-order-sidebar">
          <div
            ref={sidebarRef}
            style={{
              overflowY: "auto", maxHeight: "calc(100vh - 200px)", overflowX: "hidden",
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }} >
          {/* Instructions */}
            <div className="instructions-container" ref={instructionsRef}>
              <Row
                className="instructions-header"
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                onMouseEnter={() => setHoveredField('instructions')}
                onMouseLeave={() => setHoveredField(null)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span>Instructions</span>
                  {hoveredField === 'instructions' && (
                    <InfoTooltip
                      label="Instructions"
                      icon={<InstructionsAddIcon />}
                      message={[
                        "Include special instructions or notes from the shipper. Drivers can view these details, ranging from contact times to specific delivery or pickup requirements.",
                      ]}
                    />
                  )}
                </div>
              </Row>
            </div>

          <TextArea
            data-testid="notes-input"
            autoSize={{ minRows: 3, maxRows: 3 }}
            className="instructions-input"
            //onChange={(e) => updateOrderDetails("notes", e.target.value)}
            onChange={(e) => setNotes(e.target.value)}

            value={notes}
            style={{ marginBottom: "16px" }}
          />
          {/* Ref Numbers */}
          <ReferenceNumberSection
            handleRefNumberChange={handleRefNumberChange}
            referencesData={referencesData}
            details={details}
            setFieldsValue={setFieldsValue}
            updatedAccountId={updatedAccountId}
            availableSpace={refAvailableSpace}
            currentOrgCode = {currentOrg.code}
            orgRules={orgRules}
          />

          {/* Options Section */}
          <OptionsSection
            key={`options-${order_label_code}`}
            handleOptionsChange ={handleOptionsChange}
            vehicleTypes={vehicleTypes}
            setFieldsValue={setFieldsValue}
            updatedAccountId={updatedAccountId}
            details={details}
            availableSpace={optionsAvailableSpace}
            order_label_code={order_label_code}
            screenMode={screenMode}
            conditionForOPnOD={conditionForOPnOD}
            orgSettings={orgSettings}
            isMultiDay={isMultiDay}
            hazmatClasses={hazmatClasses}
            tags={tags}
            setTags={setTags}
            bondedOrderChecked={bondedOrderChecked}
            setBondedOrderChecked={setBondedOrderChecked}
            inboundChecked={inboundChecked}
            customClearanceChecked={customClearanceChecked}
            openInboundModal={openInboundModal}
            openCustomClearanceModal={openCustomClearanceModal}
            handleInboundCheckboxChange={handleInboundCheckboxChange}
            handleCustomClearanceCheckboxChange={handleCustomClearanceCheckboxChange}
            delivery_location_details={delivery_location_details}
            readyForCustomerChecked={readyForCustomerChecked}
            setReadyForCustomerChecked={setReadyForCustomerChecked}
            agentId={agentId}
            savedCustomClearanceDate={savedCustomClearanceDate}
            UNNumbers={UNNumber}
            allDocsUploaded={allDocsUploaded}
           />

          {/* Tags Section*/}
          <div className="row-container" ref={tagsRef}>
            <Row
              className="TagSectionContainer"
              gutter={8}
              type="flex"
              justify="space-between"
              align="middle"
            >
                <Col >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}  onMouseEnter={() => setHoveredField('tags')}
              onMouseLeave={() => setHoveredField(null)} >
                    <span className="heading-3-large">Tags</span>
                    {hoveredField === 'tags' && (
                      <InfoTooltip
                        label="Tags"
                        icon={<TagsTooltipIcon />}
                        message={[
                          "Tags help you categorize orders for searching and route planning. They can also mark important details you need to know about an order.",
                        ]}
                      />
                    )}
                  </div>
                </Col>
              <Col>
                <Button_v2
                  data-testid="add-tag-button"
                  className="small-btn flat"
                  type="link"
                  onClick={openModal}
                  hoverColor="#576ec1"
                >
                  Add Tag
                  <div className="hover-overlay"></div>
                </Button_v2>
              </Col>
            </Row>
          </div>
          <Modal
            className="TagSectionContainer"
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                Add Tags
                <TagsModalCloseIcon
                  onClick={() => setIsModalVisible(false)}
                />
              </div>
            }
            visible={isModalVisible}
            onCancel={() => setIsModalVisible(false)}
            centered={false}
            footer={
              sortedAvailableTags.length === 0 && tagInput ? null : (
                <div
                  style={{
                    position: "relative",
                    top: "8px",
                    maxHeight: "24px",
                    height: "20px",
                    overflow: "hidden",
                  }}
                  data-testid="save-tags-button"
                >
                  <span className="button-2-large" onClick={addSelectedTags}>
                    {selectedTags.length > 1 ? "Add Tags" : "Add Tag"}
                  </span>
                </div>
              )
            }
            
            width={224}
            closable={false}
            style={{
              position: "absolute",
              top: modalPosition.top,
              left: modalPosition.left,
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <Input
              data-testid="tag-name-input"
              style={{ height: "40px", borderRadius: "4px" }}
              placeholder="Tag Name"
              value={tagInput}
              onChange={handleTagInputChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleTagSubmit();
                }
              }}
            />

            <div
              style={{
                marginTop: "16px",
                maxHeight: "202px", 
                overflowY: "auto", 
                border: "1px solid #e0e0e0", 
                borderRadius: "4px",
              }}
            >
              {sortedAvailableTags.map((tag, index) => (
                <div
                  key={tag}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    height: "40px",
                    padding: "0 8px",
                  }}
                  data-testid={`tags-checkbox-${index}`}
                >
                  <span className="TagStyle">{tag}</span>
                  <Checkbox
                    style={{ color: "#576ec1", borderRadius: "4px" }}
                    checked={selectedTags.includes(tag)}
                    onChange={() => handleTagCheckboxChange(tag)}
                  />
                </div>
              ))}
              {/* Option to add a new tag */}
              {!sortedAvailableTags.some(
                (tag) => tag.toLowerCase() === tagInput.toLowerCase()
              ) && tagInput && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontWeight: "bold",
                      cursor: "pointer",
                      marginTop: "8px",
                      padding: "0 8px",
                    }}
                    data-testid="click-tag"
                    onClick={handleTagSubmit}
                  >
                    <span>Add '{tagInput}' Tag</span>
                  </div>
                )}
            </div>
          </Modal>
          <Row style={{ paddingBottom: "16px" }}>
            <div className="tags-section">{filteredTags}</div>
          </Row>
        </div> 
        {/* Save as Template and CreateOrder Button*/}
        <div style={{ height: "120px" }} >
        <Divider style={{ margin: "0 0 16px 0" }} />
          {//orderDetails?.type_of_order !== "MS" &&
           <Row data-testid="save-as-template-checkbox">
            <Checkbox
              className={`body-2-large ${isChecked ? "bold-text" : ""}`}
              onChange={handleCheckboxChange}
              checked={isChecked}
              onMouseEnter={() => setHoveredField('saveasTemplate')}
              onMouseLeave={() => setHoveredField(null)}
            >
                <span style={{ marginRight: "10px" }}>
                  {isChecked && templateName
                    ? `Save as '${templateName}'`
                    : "Save as Template"}
                </span>

                {hoveredField === 'saveasTemplate' &&( 
                      <InfoTooltip
                        label="Templates"
                        icon={<TemplatesTooltipIcon/>}
                        message={[
                          "Check to save your order as a template for future use. This lets you quickly create similar orders by selecting from saved templates in the order creation dialog.",
                        ]}
                      />
                    )}

            </Checkbox>
          </Row>}
          <Row style={{ marginTop: '16px' }}>
            {/* Span wrapper: a native disabled button has pointer-events:none,
                so it swallows hover — the Tooltip never shows and the cursor
                never changes. The span catches the hover, so hovering the
                disabled button shows the not-allowed cursor AND the tooltip
                listing which items block the save. */}
            <Tooltip
              title={
                saveDisabled ? <span style={{ whiteSpace: "pre-line" }}>{saveDisabledReason}</span> : ""
              }
            >
              <span
                style={{
                  display: "block",
                  width: "100%",
                  cursor: saveDisabled ? "not-allowed" : "",
                }}
              >
                <Button_v2
                  data-testid={`${screenMode === "create" ?" create" : "save"}-order-btn`}
                  className="large-btn raised"
                  block
                  onClick={handleSave}
                  loading={isLoading}
                  disabled={saveDisabled}
                >
                  {screenMode === "create" ?" Create Order" : "Save Order"}
                </Button_v2>
              </span>
            </Tooltip>
          </Row>
        </div>
      </div>

      {/* Modal for Template Name */}
      <Modal
        title="Save as Template"
        visible={isTemplateModalVisible}
        onOk={handleSaveTemplate}
        onCancel={handleModalCancel}
        footer={[
          <Button_v2
            data-testid="cancel-template-button"
            key="cancel"
            className="medium-btn flat"
            type="link"
            onClick={handleModalCancel}
          >
            Cancel
          </Button_v2>,
          <Button_v2
            data-testid="save-template-button"
            key="save"
            className="medium-btn flat"
            type="link"
            onClick={handleSaveTemplate}
          >
            Save Template
          </Button_v2>,
        ]}
        centered
        width={360}
        closable={false}
        style={{ top: 20 }}
        transitionName="ant-modal-dropdown"
      >
        <div className="caption-large-emphasis" style={{ marginBottom: "1px" }}>
          Template Name
        </div>
        <Input data-testid="name-input" value={templateInput} onChange={handleTemplateInputChange} onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSaveTemplate();
          }
        }} />
        {templateError && (
          <div style={{ color: "red" }}>
            {templateError}
          </div>
        )}


        <Checkbox
          style={{
            marginTop: "16px",
            fontWeight: isRecurringOrder ? "bold" : "normal",
          }}
          onChange={handleRecurringCheckboxChange}
          checked={isRecurringOrder}
          data-testid="recurring-checkbox"
        >
          Recurring
        </Checkbox>
        {isRecurringOrder && (
          <div style={{ marginTop: "16px" }}>
            <div
              className="caption-large-emphasis"
              style={{ marginBottom: "1px" }}
            >
              Repeats
            </div>
            <Select
              value={repeatOption}
              onChange={handleRepeatChange}
              style={{ width: "100%" }}
              data-testid="repeat-option"
            >
              <Option value="Daily">Daily</Option>
              <Option value="Days">Weekly</Option>
              <Option value="Monthly">Monthly</Option>
              <Option value="Specific Date">Specific Date</Option>
            </Select>

            {repeatOption === "Days" && ( 
              <div style={{ marginTop: "16px" }}>
                <div
                  className="caption-large-emphasis"
                  style={{ marginBottom: "1px" }}
                >
                  Select Week Days
                </div>
                <Select
                  data-testid="week-dropdown"
                  mode="multiple"
                  placeholder="Select Days"
                  value={selectedDays}
                  onChange={handleDaysChange}
                  style={{ width: "100%" }}
                >
                  {weekDaysOptions.map((day) => (
                    <Option key={day} value={day}>
                      {day}
                    </Option>
                  ))}
                </Select>
              </div>
            )}

            {repeatOption === "Monthly" && (
              <div style={{ marginTop: "16px" }}>
                <div
                  className="caption-large-emphasis"
                  style={{ marginBottom: "1px" }}
                >
                  Select Day of the Month
                </div>
                <Select
                  data-testid="select-dropdown"
                  placeholder="Select Date"
                  value={selectedMonthlyDate}
                  onChange={handleMonthlyDateChange}
                  style={{ width: "100%" }}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((date) => (
                    <Option key={date} value={date}>
                      {date}
                    </Option>
                  ))}
                </Select>
              </div>
            )}

            {repeatOption === "Specific Date" && (
              <div style={{ marginTop: "16px" }}>
                <div
                  className="caption-large-emphasis"
                  style={{ marginBottom: "1px" }}
                >
                  Select Specific Date
                </div>
                <DatePicker
                  data-testid="specific-date-picker"
                  onChange={handleSpecificDateChange}
                  style={{ width: "100%" }}
                />
              </div>
            )}

            <Row>
              <div
                className="caption-large-emphasis"
                style={{ marginBottom: "1px", marginTop: "16px" }}
              >
                Exclude Dates
              </div>
              <div style={{ marginTop: "1px", marginBottom: "1px" }}>
                {selectedDates.map((date) => (
                  <Tag
                    key={date}
                    closable
                    onClose={() => removeDate(date)}
                    closeIcon={<CircledCloseIcon style={{ color: "white" }} />}
                    style={{
                      marginBottom: "8px",
                      borderColor: "#576ec1",
                      backgroundColor: "#576ec1",
                      color: "white",
                    }}
                  >
                    {date}
                  </Tag>
                ))}
              </div>
              <DatePicker
                open={pickerOpen}
                onOpenChange={handlePickerOpen}
                style={{ width: "100%" }}
                onChange={handleDateSelect}
                dateRender={dateRender}
                placeholder="Select Date"
                renderExtraFooter={() => (
                  <div style={{ textAlign: "right", padding: "10px" }}>
                    <Button type="link" onClick={handleOkClick}>
                      OK
                    </Button>
                  </div>
                )}
              />
            </Row>
            <Row>
              <div
                className="caption-large-emphasis"
                style={{ marginBottom: "1px", marginTop: "16px" }}
              >
                Include Dates
              </div>
              <div style={{ marginTop: "1px", marginBottom: "1px" }}>
                {includedDates.map((date) => (
                  <Tag
                    key={date}
                    closable
                    onClose={() => removeIncludedDate(date)}
                    closeIcon={<CircledCloseIcon style={{ color: "white" }} />}
                    style={{
                      marginBottom: "8px",
                      borderColor: "#576ec1",
                      backgroundColor: "#576ec1",
                      color: "white",
                    }}
                  >
                    {date}
                  </Tag>
                ))}
              </div>
              <DatePicker
                open={includePickerOpen}
                onOpenChange={handleIncludesPickerOpen}
                style={{ width: "100%" }}
                onChange={(date, dateString) => {
                  if (!date) return;
                  if (includedDates.includes(dateString)) {
                    setIncludedDates(
                      includedDates.filter((d) => d !== dateString)
                    );
                  } else {
                    setIncludedDates([...includedDates, dateString]);
                  }
                }}
                dateRender={includeDateRender}
                placeholder="Select Include Dates"
                renderExtraFooter={() => (
                  <div style={{ textAlign: "right", padding: "10px" }}>
                    <Button type="link" onClick={handleIncludesOkClick}>
                      OK
                    </Button>
                  </div>
                )}
              />
            </Row>
          </div>
        )}
      </Modal>

      {/* Inbound Modal */}
      <Modal
        title="Agent/Broker Details"
        visible={isInboundModalVisible}
        onOk={handleInboundSave}
        onCancel={handleInboundCancel}
        footer={[
          <Button_v2
            data-testid="cancel-inbound-button"
            key="cancel"
            className="medium-btn flat"
            type="link"
            onClick={handleInboundCancel}
          >
            Cancel
          </Button_v2>,
          <Button_v2
            data-testid="save-inbound-button"
            key="save"
            className="medium-btn flat"
            type="link"
            onClick={handleInboundSave}
            disabled={isInboundSaveDisabled}
            loading={isInboundSaving}
          >
            Save
          </Button_v2>,
        ]}
        centered
        width={360}
        closable={false}
        style={{ top: 20 }}
        transitionName="ant-modal-dropdown"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div>
            <div className="caption-large-emphasis" style={{ marginBottom: "4px" }}>
              Name
            </div>
            <Input
              data-testid="inbound-name-input"
              value={inboundName}
              onChange={(e) => setInboundName(e.target.value)}
              placeholder="Enter name"
            />
          </div>

          <div>
            <div className="caption-large-emphasis" style={{ marginBottom: "4px" }}>
              Phone No
            </div>
            <MobileInput_v2
              value={inboundPhone}
              handleInputChange={(val) => setInboundPhone(val)}
            />
          </div>

          <div>
            <div className="caption-large-emphasis" style={{ marginBottom: "4px" }}>
              Agent/Broker ID
            </div>
            <Input
              data-testid="inbound-agent-broker-id-input"
              value={inboundAgentBrokerId}
              onChange={(e) => setInboundAgentBrokerId(e.target.value)}
              placeholder="Enter Agent/Broker ID"
            />
          </div>
          <div>
            <div className="caption-large-emphasis" style={{ marginBottom: "4px" }}>
              Freight Arrival Date
            </div>
            <DatePicker
              data-testid="inbound-freight-arrival-date-picker"
              value={freightArrivalDate ? moment(freightArrivalDate) : null}
              onChange={(date) => setFreightArrivalDate(date ? date.toISOString() : null)}
              style={{ width: "100%" }}
            />
          </div>

          <div>
            <div className="caption-large-emphasis" style={{ marginBottom: "4px" }}>
              Last Free Day of Storage
            </div>
            <DatePicker
              data-testid="inbound-last-free-day-picker"
              value={lastFreeDay ? moment(lastFreeDay) : null}
              onChange={(date) => setLastFreeDay(date ? date.toISOString() : null)}
              style={{ width: "100%" }}
            />
          </div>

          <div>
            <div className="caption-large-emphasis" style={{ marginBottom: "4px" }}>
              General Order Date
            </div>
            <DatePicker
              data-testid="inbound-general-order-date-picker"
              value={generalOrderDate ? moment(generalOrderDate) : null}
              onChange={(date) => setGeneralOrderDate(date ? date.toISOString() : null)}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </Modal>

      {/* Custom Clearance Modal */}
      <Modal
        title="Customs Clearance Details"
        visible={isCustomClearanceModalVisible}
        onOk={handleCustomClearanceSave}
        onCancel={handleCustomClearanceCancel}
        footer={[
          <Button_v2
            data-testid="cancel-custom-clearance-button"
            key="cancel"
            className="medium-btn flat"
            type="link"
            onClick={handleCustomClearanceCancel}
          >
            Cancel
          </Button_v2>,
          <Button_v2
            data-testid="save-custom-clearance-button"
            key="save"
            className="medium-btn flat"
            type="link"
            onClick={handleCustomClearanceSave}
            disabled={isCustomClearanceSaveDisabled}
          >
            {allDocsUploaded ? "Customs Cleared" : "Save"}
          </Button_v2>,
        ]}
        centered
        width={550}
        closable={false}
        style={{ top: 20 }}
        transitionName="ant-modal-dropdown"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <div className="caption-large-emphasis" style={{ marginBottom: "6px" }}>
              Clearance Date
            </div>
            <DatePicker
              data-testid="clearance-date-picker"
              value={customClearanceDate ? moment(customClearanceDate) : null}
              onChange={(date, dateString) => setCustomClearanceDate(dateString)}
              style={{ width: "100%" }}
              placeholder="Select Date"
            />
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <span className="caption-large-emphasis">Required Customs Documents</span>
              {(details?.id || details?.customer_order_id || details?._id) && CUSTOMS_DOC_TYPES.some(type => customClearanceFilesMap[type.code] && !customClearanceFilesUploadedMap[type.code]) && (
                <Button
                  data-testid="upload-all-customs-docs-btn"
                  className="medium-btn flat"
                  type="link"
                  onClick={handleUploadAllCustomsFiles}
                  loading={isUploadingAll}
                  style={{ display: "flex", alignItems: "center", gap: "4px", height: "28px", fontSize: "12px", padding: "0 8px" }}
                >
                  {!isUploadingAll && <Icon type="upload" />}
                  <span>Upload</span>
                </Button>
              )}
            </div>
            <div style={{ maxHeight: "300px", overflowY: "auto", paddingRight: "4px" }}>
              {CUSTOMS_DOC_TYPES.map((type) => {
                const existingDoc = existingCustomsDocsMap[type.code];
                const file = customClearanceFilesMap[type.code];
                const isUploaded = customClearanceFilesUploadedMap[type.code];
                const isUploading = isUploadingCustomClearanceFileMap[type.code];
                const orderId = details?.id || details?.customer_order_id || details?._id || "";

                return (
                  <div
                    key={type.code}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      background: "#fafafa",
                      border: "1px solid #f0f0f0",
                      borderRadius: "6px",
                      marginBottom: "8px"
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px", maxWidth: "55%" }}>
                      <span className="caption-large-emphasis" style={{ textTransform: "capitalize", color: "#111314", fontSize: "12px" }}>
                        {type.label}
                      </span>
                    </div>
                    <div>
                      {existingDoc ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#f6ffed", border: "1px solid #b7eb8f", padding: "4px 8px", borderRadius: "4px" }}>
                          <span
                            style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "11px", color: "#389e0d", textDecoration: "underline", cursor: "pointer" }}
                            onClick={() => {
                              const url = existingDoc.picture_url;
                              const isPdf = url?.toLowerCase().endsWith(".pdf");
                              setDocPreviewUrl(url);
                              setDocPreviewType(isPdf ? "pdf" : "image");
                              setDocPreviewVisible(true);
                            }}
                          >
                            <Icon type={existingDoc.picture_url?.toLowerCase().endsWith(".pdf") ? "file-pdf" : "file-image"} style={{ marginRight: "4px", color: "#52c41a" }} />
                            {existingDoc.picture_url.split("/").pop() || "Document"}
                          </span>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <Icon type="check-circle" theme="filled" style={{ color: "#52c41a", fontSize: "13px" }} />
                            <Tooltip title="Remove">
                              <Icon
                                type="delete"
                                style={{ color: "#ff4d4f", cursor: "pointer", fontSize: "13px" }}
                                onClick={() => handleRemoveExistingDoc(type.code, existingDoc.ack_id)}
                              />
                            </Tooltip>
                          </div>
                        </div>
                      ) : file ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#e6f7ff", border: "1px solid #91d5ff", padding: "4px 8px", borderRadius: "4px" }}>
                          <span style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "11px", color: "#0050b3" }}>
                            <Icon type="paper-clip" style={{ marginRight: "4px" }} />
                            {file.name}
                          </span>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <Tooltip title="Preview">
                              <Icon
                                type="eye"
                                style={{ color: "#1890ff", cursor: "pointer", fontSize: "13px" }}
                                onClick={() => {
                                  const objectUrl = URL.createObjectURL(file);
                                  const isPdf = file?.type === "application/pdf" || file?.name?.toLowerCase().endsWith(".pdf");
                                  setDocPreviewUrl(objectUrl);
                                  setDocPreviewType(isPdf ? "pdf" : "image");
                                  setDocPreviewVisible(true);
                                }}
                              />
                            </Tooltip>
                            {(isUploaded || !orderId) && (
                              <Icon type="check-circle" theme="filled" style={{ color: "#52c41a", fontSize: "13px" }} />
                            )}
                            <Tooltip title="Remove">
                              <Icon
                                type="delete"
                                style={{ color: "#ff4d4f", cursor: "pointer", fontSize: "13px" }}
                                onClick={() => handleRemoveFile(type.code)}
                              />
                            </Tooltip>
                          </div>
                        </div>
                      ) : (
                        <Upload
                          beforeUpload={(f) => {
                            handleFileSelect(f, type.code);
                            return false;
                          }}
                          showUploadList={false}
                        >
                          <Button style={{ display: "flex", alignItems: "center", gap: "4px", borderRadius: "4px", height: "28px", fontSize: "11px", border: "1px dashed #d9d9d9", color: "#576ec1", background: "#f9fbfd" }}>
                            <Icon type="upload" /> Select File
                          </Button>
                        </Upload>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>

      {/* Doc Preview Modal */}
      <Modal
        visible={docPreviewVisible}
        footer={null}
        onCancel={() => setDocPreviewVisible(false)}
        centered
        width={docPreviewType === "pdf" ? 800 : "auto"}
        bodyStyle={{ padding: 0, textAlign: "center" }}
        title={
          <span style={{ fontSize: "13px", color: "#576ec1" }}>
            {docPreviewUrl?.split("/").pop() || "Document Preview"}
          </span>
        }
      >
        {docPreviewType === "image" ? (
          <img
            src={docPreviewUrl}
            alt="Clearance Document"
            style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }}
          />
        ) : docPreviewType === "pdf" ? (
          <iframe
            src={docPreviewUrl}
            title="Clearance PDF"
            style={{ width: "100%", height: "80vh", border: "none" }}
          />
        ) : null}
      </Modal>

      <ApprovalRequiredModal 
        visible={approvalModal}
        onCancel={() => hanldeCloseApprovalModal(false)}
        onRequestApproval={handleRequestApproval}
        restrictionMessage={restrictionMessage}
        reqLoading={isLoading}
      />
    </Layout>
    </div>
  );
};

export default CreateOrderSidebar;